import { AddOutlined, Close, EditOutlined } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';

import { useFetchData } from '../../hooks/useFetchData';
import { getNotes, saveNotes } from '../../services/dealGPT';
import { ProjectNotes, ProjectScopeAreaType, ProjectWithLastScanned } from '../../Types/dealGPT';
import { analysisScopesPathUrl } from '../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

type FilterProps = {
  selected?: boolean;
};

const Filter = styled(Typography)<FilterProps>(({ selected = false }) => ({
  height: 'fit-content',
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '125%',
  color: '#092B49',
  backgroundColor: selected ? '#CED7E6' : '#E8EEF8',
  borderRadius: '100px',
  padding: '2px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}));

const FiltersContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  margin: 0,
  maxWidth: 'none !important',

  [theme.breakpoints.up('sm')]: {
    padding: '0px',
  },
}));

const FiltersPopover = styled(Popover)({
  '& .MuiPaper-root': {
    padding: '16px 0px',
    width: '40%',
    border: '1px solid #C4C4C4',
  },
});

const StyledContainer = styled('div')({
  padding: '16px',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '16px',
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '950px',
    maxWidth: '950px',
  },
});

const StyledSelectedFilter = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '10px',
  alignItems: 'center',
});

export type ScopeAreasFilterViewProps = {
  project: ProjectWithLastScanned;
  selectedScopes: ProjectScopeAreaType[];
  setSelectedScopes: (scopes: ProjectScopeAreaType[]) => void;
};

const ScopeAreasFilterView = ({
  project,
  selectedScopes,
  setSelectedScopes,
}: ScopeAreasFilterViewProps) => {
  const { data: projectScopeAreas, loading: isLoadingScopeAreas } = useFetchData<
    ProjectScopeAreaType[]
  >({
    urlPath: analysisScopesPathUrl(project.projectType, project.id),
  });
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [projectNotes, setProjectNotes] = useState<ProjectNotes>();
  const [openNoteDialog, setOpenNoteDialog] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLInputElement>();
  const analytics = useAnalytics();
  const [filterContextMenu, setfilterContextMenu] = useState<null | HTMLElement>(null);

  const handleOpenFilterContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    setfilterContextMenu(event.currentTarget);
  };

  const handleCloseContextMenu = () => {
    setfilterContextMenu(null);
  };

  const isScopeAreaSelected = (scope: ProjectScopeAreaType) => {
    return selectedScopes?.includes(scope);
  };

  const toggleScopeFilter = (scope: ProjectScopeAreaType) => {
    const isScopeSelected = isScopeAreaSelected(scope);
    if (isScopeSelected) {
      setSelectedScopes(selectedScopes.filter((prevScope) => prevScope !== scope));
    } else {
      setSelectedScopes([...selectedScopes, scope]);
    }

    analytics.event(
      AnalyticsEvent.WM_IA_CHAT_SITE_NOTES_FILTER,
      {
        id: project.id,
        value: `Scope=${scope},Selected=${!isScopeSelected}`,
      },
      {
        projectId: project.id,
        scope: scope.scope,
        scopeWasSelected: isScopeSelected,
        scopeNowSelected: !isScopeSelected,
      }
    );
  };

  const handleCloseDialog = () => {
    setOpenNoteDialog(false);
  };

  const getUserInput = () => {
    if (textAreaRef.current) {
      return textAreaRef.current.value;
    }
    return '';
  };

  const handleOpenNoteDialog = async (scopeArea: ProjectScopeAreaType) => {
    if (project) {
      try {
        setIsLoadingNotes(true);
        setOpenNoteDialog(true);

        analytics.event(
          AnalyticsEvent.WM_IA_CHAT_SITE_NOTES_EDIT,
          {
            id: project.id,
            value: `Scope=${scopeArea.scope}`,
          },
          {
            projectId: project.id,
            scope: scopeArea.scope,
          }
        );

        //Fetching Notes for current project and scope Area
        const notes = await getNotes(project.id, scopeArea.scope);
        if (notes) {
          setProjectNotes(notes);
        } else {
          setProjectNotes({
            projectId: project.id,
            notes: '',
            scope: scopeArea.scope,
          });
        }
      } catch (error) {
        handleCloseDialog();
      }
      setIsLoadingNotes(false);
    }
  };

  const handleSaveNote = async () => {
    const newNote = projectNotes;
    if (newNote) {
      newNote.notes = getUserInput();

      analytics.event(
        AnalyticsEvent.WM_IA_CHAT_SITE_NOTES_EDIT_SAVE,
        {
          id: project.id,
          value: `Scope=${newNote.scope},Notes=${newNote.notes}`,
        },
        {
          projectId: project.id,
          scope: newNote.scope,
          oldScopeNotes: projectNotes?.notes,
          newScopeNotes: newNote.notes,
        }
      );

      // Save notes to the database
      try {
        await saveNotes(newNote, false);
        enqueueSnackbar('Notes saved successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to save notes', { variant: 'error' });
      }
    }
    handleCloseDialog();
  };

  return (
    <FiltersContainer>
      {projectScopeAreas && projectScopeAreas.length > 0 && (
        <>
          <Tooltip title="Add Site Visit Notes">
            <IconButton onClick={handleOpenFilterContextMenu}>
              <AddOutlined />
              <Typography
                sx={{
                  fontSize: '16px',
                  alignSelf: 'center',
                }}
              >
                Include Site Visit Notes
              </Typography>
            </IconButton>
          </Tooltip>
          <StyledSelectedFilter>
            {selectedScopes.length > 3 ? (
              <>
                {selectedScopes.slice(0, 3).map((scope) => (
                  <Filter key={scope.scope} selected={true} sx={{ cursor: 'default' }}>
                    {scope.scope}
                  </Filter>
                ))}
                <span style={{ fontSize: '30px', paddingBottom: '6px' }}>...</span>
              </>
            ) : (
              selectedScopes.map((scope) => (
                <Filter key={scope.scope} selected={true} sx={{ cursor: 'default' }}>
                  {scope.scope}
                </Filter>
              ))
            )}
          </StyledSelectedFilter>
        </>
      )}
      {isLoadingScopeAreas ? (
        <CircularProgress sx={{ margin: 'auto' }} />
      ) : (
        projectScopeAreas &&
        projectScopeAreas.length > 0 && (
          <FiltersPopover
            sx={{ mt: '25px' }}
            id="menu-scopesfilter"
            anchorEl={filterContextMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            open={Boolean(filterContextMenu)}
            onClose={handleCloseContextMenu}
          >
            <Typography
              variant="h6"
              sx={{
                paddingLeft: '16px',
              }}
            >
              Scope Areas
            </Typography>
            <StyledContainer>
              {projectScopeAreas.map((scope) => (
                <Filter
                  key={scope.scope}
                  selected={isScopeAreaSelected(scope)}
                  onClick={() => toggleScopeFilter(scope)}
                >
                  {scope.scope}
                  <IconButton
                    aria-label="Edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNoteDialog(scope);
                    }}
                    sx={{ padding: '0' }}
                  >
                    <EditOutlined sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Filter>
              ))}
            </StyledContainer>
          </FiltersPopover>
        )
      )}
      <StyledDialog open={openNoteDialog} onClose={handleCloseDialog}>
        <DialogTitle>{projectNotes?.scope} notes</DialogTitle>
        <IconButton
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#757575',
          }}
        >
          <Close />
        </IconButton>
        <DialogContent>
          {isLoadingNotes ? (
            <CircularProgress sx={{ margin: 'auto' }} />
          ) : (
            <TextField
              inputRef={(ref) => (textAreaRef.current = ref)}
              defaultValue={projectNotes?.notes}
              name="notes"
              multiline
              sx={{ minWidth: '100%', maxWidth: '100%' }}
              rows={4}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ marginBottom: '10px' }}
            variant="contained"
            disabled={isLoadingNotes}
            onClick={handleSaveNote}
          >
            Save
          </Button>
          <Button
            sx={{ marginBottom: '10px', marginRight: '15px' }}
            variant="outlined"
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
        </DialogActions>
      </StyledDialog>
    </FiltersContainer>
  );
};

export default ScopeAreasFilterView;
